import type { Edge, Node } from '@xyflow/react';
import type { MailingChannel } from './mailingTypes';
import { normalizeChannelNodeData } from './mailingTypes';

const KNOWN_CHANNELS: readonly MailingChannel[] = ['push', 'sms', 'email', 'viber', 'waba'];

function channelFromNode(node: Node): MailingChannel | null {
  if (String(node.type) !== 'mailingChannel') return null;
  const d = normalizeChannelNodeData(node.data);
  return KNOWN_CHANNELS.includes(d.channel) ? d.channel : null;
}

function edgesOut(edges: Edge[], sourceId: string, sourceHandle?: string | null): Edge[] {
  return edges.filter((e) => {
    if (e.source !== sourceId) return false;
    const sh = e.sourceHandle ?? '';
    if (sourceHandle === undefined || sourceHandle === null || sourceHandle === '') {
      return sh === '' || sh === 'out';
    }
    return sh === sourceHandle;
  });
}

function firstTarget(edges: Edge[], sourceId: string, handle?: string | null): string | null {
  const outs = edgesOut(edges, sourceId, handle);
  if (!outs.length) return null;
  const t = outs[0]?.target;
  return t ? String(t) : null;
}

/** Узлы каналов по порядку обхода от Старт (как на сервере в linearize_visual_channels). */
export type VisualChainChannelStep = { nodeId: string; channel: MailingChannel };

export function linearizeVisualChannelSteps(nodes: Node[], edges: Edge[]): VisualChainChannelStep[] {
  const starts = nodes.filter((n) => String(n.type) === 'mailingStart');
  if (starts.length !== 1) return [];
  let cur = firstTarget(edges, String(starts[0].id), 'out');
  if (!cur) return [];
  const visited = new Set<string>();
  const out: VisualChainChannelStep[] = [];
  for (let i = 0; i < 200; i += 1) {
    if (!cur || visited.has(cur)) break;
    visited.add(cur);
    const node = nodes.find((n) => String(n.id) === cur);
    if (!node) break;
    const nt = String(node.type || '');
    if (nt === 'mailingEnd') break;
    let nxt: string | null = null;
    if (nt === 'mailingChannel') {
      const ch = channelFromNode(node);
      if (ch) out.push({ nodeId: cur, channel: ch });
      nxt = firstTarget(edges, cur, null);
    } else if (nt === 'mailingCondition') {
      nxt = firstTarget(edges, cur, 'yes') || firstTarget(edges, cur, 'no');
    } else if (nt === 'mailingAbTest') {
      nxt = firstTarget(edges, cur, 'a') || firstTarget(edges, cur, 'b');
    } else if (nt === 'mailingDelay') {
      nxt =
        firstTarget(edges, cur, null)
        || firstTarget(edges, cur, 'delivered')
        || firstTarget(edges, cur, 'not_delivered');
    } else {
      nxt = firstTarget(edges, cur, null);
    }
    if (!nxt) break;
    cur = nxt;
  }
  return out;
}

/** Полный путь от Старт до Конец (узлы по порядку обхода — как linearizeVisualChannelSteps). */
export function linearizeVisualPathNodes(nodes: Node[], edges: Edge[]): Node[] {
  const starts = nodes.filter((n) => String(n.type) === 'mailingStart');
  if (starts.length !== 1) return [];
  let cur = firstTarget(edges, String(starts[0].id), 'out');
  if (!cur) return [];
  const visited = new Set<string>();
  const out: Node[] = [];
  for (let i = 0; i < 200; i += 1) {
    if (!cur || visited.has(cur)) break;
    visited.add(cur);
    const node = nodes.find((n) => String(n.id) === cur);
    if (!node) break;
    out.push(node);
    const nt = String(node.type || '');
    if (nt === 'mailingEnd') break;
    let nxt: string | null = null;
    if (nt === 'mailingChannel') {
      nxt = firstTarget(edges, cur, null);
    } else if (nt === 'mailingCondition') {
      nxt = firstTarget(edges, cur, 'yes') || firstTarget(edges, cur, 'no');
    } else if (nt === 'mailingAbTest') {
      nxt = firstTarget(edges, cur, 'a') || firstTarget(edges, cur, 'b');
    } else if (nt === 'mailingDelay') {
      nxt =
        firstTarget(edges, cur, null)
        || firstTarget(edges, cur, 'delivered')
        || firstTarget(edges, cur, 'not_delivered');
    } else {
      nxt = firstTarget(edges, cur, null);
    }
    if (!nxt) break;
    cur = nxt;
  }
  return out;
}

/** Линейная цепочка ровно из двух шагов: SMS и Viber, без других каналов (Email, Push, …). */
export function isVisualChainViberSmsDyadNoEmail(nodes: Node[], edges: Edge[]): boolean {
  const steps = linearizeVisualChannelSteps(nodes, edges);
  if (steps.length !== 2) return false;
  const a = steps[0]!.channel;
  const b = steps[1]!.channel;
  if (a === b) return false;
  const set = new Set<MailingChannel>([a, b]);
  return set.has('sms') && set.has('viber');
}
